import { useMemo, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import {
  FaCloudUploadAlt,
  FaFileExcel,
  FaDownload,
  FaPaperPlane,
  FaEraser,
  FaCheckCircle,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaFileArchive,
  FaTable,
  FaInfoCircle,
} from 'react-icons/fa';
import Rut from 'rutjs';
import * as XLSX from 'xlsx';
import api from '../../api/apiweb-massiveFacturaUploader.js';
import '../emisorMasivo/EmisorMasivo.scss';

// Tipo de documento de referencia para Orden de Compra y HES (ver constants/tiposReferencia.js)
const TIPO_REF_ORDEN_COMPRA = '801';
// HES se envía como código de texto libre "HES" (no el numérico 816).
const TIPO_REF_HES = 'HES';

// Columnas del Excel. El orden define la plantilla y la vista previa.
// Las columnas de montos (iva / valor total) dependen de si es afecta o exenta.
// La descripción va junto al nombre de producto. Su valor puede quedar en blanco,
// pero la columna siempre forma parte del layout para facilitar el llenado.
const RECEPTOR_PRODUCT_COLUMNS = [
  'rut',
  'razon social',
  'giro',
  'direccion',
  'comuna',
  'ciudad',
  'email',
  'fecha emision',
  'nombre producto',
  'descripcion',
  'cantidad',
  'unidad',
  'precio',
];

const REFERENCE_COLUMNS = [
  'folio orden compra',
  'fecha orden compra',
  'razon orden compra',
  'folio hes',
  'fecha hes',
  'razon hes',
];

// Columnas cuyo valor es opcional (la columna debe existir igual).
const VALUE_OPTIONAL_COLUMNS = new Set(['email', 'descripcion', 'razon orden compra', 'razon hes']);

const COLUMN_LABELS = {
  'rut': 'RUT',
  'razon social': 'Razón Social',
  'giro': 'Giro',
  'direccion': 'Dirección',
  'comuna': 'Comuna',
  'ciudad': 'Ciudad',
  'email': 'Email',
  'fecha emision': 'Fecha Emisión',
  'nombre producto': 'Nombre Producto',
  'descripcion': 'Descripción',
  'cantidad': 'Cantidad',
  'unidad': 'Unidad',
  'precio': 'Precio',
  'iva': 'IVA',
  'valor total': 'Valor Total',
  'forma pago': 'Forma de Pago',
  'folio orden compra': 'Folio Orden Compra',
  'fecha orden compra': 'Fecha Orden Compra',
  'razon orden compra': 'Razón Orden Compra',
  'folio hes': 'Folio HES',
  'fecha hes': 'Fecha HES',
  'razon hes': 'Razón HES',
};

const NUM_COLUMNS = new Set(['cantidad', 'precio', 'iva', 'valor total']);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeKey = (key) =>
  key
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\s_]+/g, ' ');

const getColumnValue = (row, aliases) => {
  for (const alias of aliases) {
    if (row[alias] !== undefined && row[alias] !== '') return row[alias];
  }
  return '';
};

const sanitizeText = (value, maxLength) => {
  let text = String(value ?? '').trim();
  text = text.normalize('NFD').replace(/[̀-ͯ]/g, '');
  text = text.replace(/ñ/gi, 'N');
  text = text.replace(/[^A-Za-z0-9\s.,-]/g, '');
  if (maxLength) text = text.slice(0, maxLength);
  return text;
};

const formatAndValidateDate = (value) => {
  if (!value) return { formatted: '', validFormat: false, isNotFuture: false };
  let formatted = '';
  if (value instanceof Date) {
    formatted = value.toISOString().split('T')[0];
  } else if (typeof value === 'number') {
    const date = XLSX.SSF.parse_date_code(value);
    if (!date) return { formatted: '', validFormat: false, isNotFuture: false };
    formatted = `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
  } else {
    const cleaned = String(value).trim().replace(/[–—]/g, '-').replace(/\s+/g, '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
      return { formatted: cleaned, validFormat: false, isNotFuture: false };
    }
    formatted = cleaned;
  }
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;
  return { formatted, validFormat: true, isNotFuture: formatted <= todayStr };
};

// Solo se aceptan contado o crédito.
const FORMA_PAGO_MAP = {
  'contado': '1',
  'credito': '2',
  'crédito': '2',
};

const FORMA_PAGO_LABEL = { '1': 'Contado', '2': 'Crédito' };

const processRut = (rut) => {
  try {
    const parsed = new Rut(rut);
    if (!parsed.validate()) return { valid: false };
    return { valid: true, rutWithoutDv: parsed.rut, rutFormatted: parsed.getNiceRut() };
  } catch {
    return { valid: false };
  }
};

/**
 * Emisor masivo de facturas. Reutilizable para afecta (33) y exenta (34) según la prop tipoDocumento.
 * El backend recibe filas planas y arma los DTE + asigna folios (endpoint massivefacturauploader).
 */
const EmisorMasivoFactura = ({ tipoDocumento = '33' }) => {
  const esExenta = tipoDocumento === '34';
  const tipoLabel = esExenta ? 'Exenta' : 'Afecta';

  // En afecta el usuario ingresa el IVA; en exenta no aplica IVA.
  const requiredColumns = useMemo(
    () =>
      esExenta
        ? [...RECEPTOR_PRODUCT_COLUMNS, 'valor total', 'forma pago', ...REFERENCE_COLUMNS]
        : [...RECEPTOR_PRODUCT_COLUMNS, 'iva', 'valor total', 'forma pago', ...REFERENCE_COLUMNS],
    [esExenta],
  );

  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessageModal, setErrorMessageModal] = useState('');
  const [processedFolios, setProcessedFolios] = useState([]);
  const [processedDocs, setProcessedDocs] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const processFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const rawData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
      const normalizedData = rawData.map((row) => {
        const normalizedRow = {};
        Object.keys(row).forEach((key) => {
          normalizedRow[normalizeKey(key)] = row[key];
        });
        return normalizedRow;
      });
      validateAndSetData(normalizedData);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileUpload = (event) => processFile(event.target.files[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      processFile(file);
    } else {
      setErrors(['Solo se aceptan archivos .xlsx o .xls']);
    }
  };

  const validateAndSetData = (data) => {
    const headers = Object.keys(data[0] || {});
    const missingCols = requiredColumns.filter(
      (col) => !VALUE_OPTIONAL_COLUMNS.has(col) && !headers.includes(col),
    );
    if (missingCols.length > 0) {
      setErrors([`Faltan las siguientes columnas: ${missingCols.join(', ')}`]);
      setRows([]);
      return;
    }

    const validatedRows = data.map((row) => {
      const rowErrors = [];

      // Receptor: RUT
      const { valid, rutWithoutDv, rutFormatted } = processRut(row['rut']);
      if (!valid) {
        rowErrors.push('RUT inválido');
      } else {
        row['rut_display'] = rutFormatted;
        row['rut'] = rutWithoutDv;
      }

      // Receptor: razón social, giro, dirección, comuna, ciudad
      const razon = sanitizeText(row['razon social'], 100);
      if (!razon) rowErrors.push('Razón social vacía');
      row['razon social'] = razon;

      const giro = sanitizeText(row['giro'], 80);
      if (!giro) rowErrors.push('Giro vacío');
      row['giro'] = giro;

      const direccion = sanitizeText(row['direccion'], 70);
      if (!direccion) rowErrors.push('Dirección vacía');
      row['direccion'] = direccion;

      const comuna = sanitizeText(row['comuna'], 20);
      if (!comuna) rowErrors.push('Comuna vacía');
      row['comuna'] = comuna.toUpperCase();

      const ciudad = sanitizeText(row['ciudad'], 20);
      if (!ciudad) rowErrors.push('Ciudad vacía');
      row['ciudad'] = ciudad;

      // Email opcional: puede venir vacío. Si trae valor, se valida formato y longitud.
      const email = String(getColumnValue(row, ['email', 'correo']) || '').trim();
      if (email) {
        if (!EMAIL_REGEX.test(email)) rowErrors.push('Email inválido');
        else if (email.length > 80) rowErrors.push('Email excede 80 caracteres');
      }
      row['email'] = email;

      // Producto
      const nombreProducto = sanitizeText(row['nombre producto'], 80);
      if (!nombreProducto) rowErrors.push('Nombre de producto vacío');
      row['nombre producto'] = nombreProducto;

      row['descripcion'] = sanitizeText(getColumnValue(row, ['descripcion']), 1000);
      row['unidad'] = String(row['unidad'] || '').trim().slice(0, 4);

      const cantidad = parseFloat(row['cantidad']);
      if (isNaN(cantidad) || cantidad <= 0) rowErrors.push('Cantidad inválida');
      row['cantidad'] = isNaN(cantidad) ? '' : cantidad;

      const precio = parseFloat(row['precio']);
      if (isNaN(precio) || precio <= 0) rowErrors.push('Precio inválido');
      row['precio'] = isNaN(precio) ? '' : precio;

      // Neto = cantidad * precio
      const neto = !isNaN(cantidad) && !isNaN(precio) ? Math.round(cantidad * precio) : 0;

      // IVA: lo ingresa el usuario (solo afecta). En exenta no aplica (queda en 0).
      let iva = NaN;
      if (!esExenta) {
        iva = parseFloat(row['iva']);
        if (isNaN(iva) || iva < 0) rowErrors.push('IVA inválido');
        row['iva'] = isNaN(iva) ? '' : iva;
      }
      const ivaFinal = esExenta ? 0 : iva; // NaN si es afecta sin IVA válido

      // Valor total ingresado por el usuario: debe cuadrar con neto + IVA.
      const valorTotal = parseFloat(row['valor total']);
      const esperado = neto + (isNaN(ivaFinal) ? 0 : ivaFinal);
      if (isNaN(valorTotal) || valorTotal < 0) {
        rowErrors.push('Valor total inválido');
      } else if (!isNaN(ivaFinal) && valorTotal !== esperado) {
        rowErrors.push(esExenta ? 'Valor total no cuadra (= neto)' : 'Valor total no cuadra (neto + IVA)');
      }
      row['valor total'] = isNaN(valorTotal) ? '' : valorTotal;

      row['_neto'] = neto;
      row['_iva'] = isNaN(ivaFinal) ? 0 : ivaFinal;
      row['_total'] = isNaN(valorTotal) ? esperado : valorTotal;

      // Forma de pago: obligatoria, solo contado o crédito.
      const rawPago = String(getColumnValue(row, ['forma pago', 'tipo pago']) || '').trim().toLowerCase();
      const codigoPago = FORMA_PAGO_MAP[rawPago];
      if (!rawPago) rowErrors.push('Forma de pago faltante');
      else if (!codigoPago) rowErrors.push('Forma de pago inválida (contado o crédito)');
      row['_forma_pago'] = codigoPago || '';
      row['forma pago'] = codigoPago ? FORMA_PAGO_LABEL[codigoPago] : row['forma pago'];

      // Fecha emisión (no futura)
      const fechaEmisionRes = formatAndValidateDate(getColumnValue(row, ['fecha emision']));
      row['fecha emision'] = fechaEmisionRes.formatted;
      if (!fechaEmisionRes.formatted) rowErrors.push('Fecha de emisión faltante');
      else if (!fechaEmisionRes.validFormat) rowErrors.push('Fecha emisión inválida (yyyy-mm-dd)');
      else if (!fechaEmisionRes.isNotFuture) rowErrors.push('Fecha emisión mayor a hoy');

      // Referencia 1: Orden de Compra
      const folioOC = String(row['folio orden compra'] || '').trim();
      if (!folioOC) rowErrors.push('Folio Orden de Compra faltante');
      row['folio orden compra'] = folioOC;
      const fechaOCRes = formatAndValidateDate(getColumnValue(row, ['fecha orden compra']));
      row['fecha orden compra'] = fechaOCRes.formatted;
      if (!fechaOCRes.formatted) rowErrors.push('Fecha Orden de Compra faltante');
      else if (!fechaOCRes.validFormat) rowErrors.push('Fecha OC inválida (yyyy-mm-dd)');
      else if (!fechaOCRes.isNotFuture) rowErrors.push('Fecha OC mayor a hoy');
      row['razon orden compra'] = sanitizeText(getColumnValue(row, ['razon orden compra']), 90);

      // Referencia 2: HES
      const folioHES = String(row['folio hes'] || '').trim();
      if (!folioHES) rowErrors.push('Folio HES faltante');
      row['folio hes'] = folioHES;
      const fechaHESRes = formatAndValidateDate(getColumnValue(row, ['fecha hes']));
      row['fecha hes'] = fechaHESRes.formatted;
      if (!fechaHESRes.formatted) rowErrors.push('Fecha HES faltante');
      else if (!fechaHESRes.validFormat) rowErrors.push('Fecha HES inválida (yyyy-mm-dd)');
      else if (!fechaHESRes.isNotFuture) rowErrors.push('Fecha HES mayor a hoy');
      row['razon hes'] = sanitizeText(getColumnValue(row, ['razon hes']), 90);

      return { ...row, error: rowErrors.join('; ') };
    });

    setErrors([]);
    setRows(validatedRows);
  };

  const buildPayloadRow = (row) => ({
    tipo_documento: tipoDocumento,
    fecha_emision: row['fecha emision'],
    forma_pago: row['_forma_pago'],
    receptor: {
      rut: row.rut,
      razon_social: row['razon social'],
      giro: row.giro,
      direccion: row.direccion,
      comuna: row.comuna,
      ciudad: row.ciudad,
      tipo_compra: '1',
      email: row.email,
    },
    producto: {
      nombre: row['nombre producto'],
      descripcion: row.descripcion || '',
      cantidad: row.cantidad,
      unidad: row.unidad || '',
      precio: row.precio,
    },
    totales: esExenta
      ? { exento: row._total, total: row._total }
      : { neto: row._neto, iva: row._iva, total: row._total },
    referencias: [
      {
        tipo: TIPO_REF_ORDEN_COMPRA,
        folio: row['folio orden compra'],
        fecha: row['fecha orden compra'],
        razon: row['razon orden compra'] || 'ORDEN DE COMPRA',
      },
      {
        tipo: TIPO_REF_HES,
        folio: row['folio hes'],
        fecha: row['fecha hes'],
        razon: row['razon hes'] || 'HES',
      },
    ],
  });

  const handleSend = () => {
    const hasErrores = rows.some((r) => r.error);
    if (hasErrores) {
      setErrorMessageModal('Algunas filas contienen errores. Revisa los datos y corrige los problemas antes de enviar.');
      setShowErrorModal(true);
      return;
    }
    const payload = JSON.stringify(rows.map(buildPayloadRow));
    setLoading(true);
    api.uploadFacturas(payload, esExenta)
      .then((response) => {
        const data = response.data?.body ? JSON.parse(response.data.body) : response.data;
        // El backend devuelve el arreglo de documentos generados; ese mismo arreglo se usa para el ZIP.
        const documentos = Array.isArray(data)
          ? data
          : (data?.documentos || data?.facturas || data?.folios || data?.assignedFolios || []);
        const folios = documentos
          .map((d) => (d && typeof d === 'object' ? (d.folio ?? d.Folio ?? d.FOLIO) : d))
          .filter((f) => f !== undefined && f !== null && f !== '');
        setProcessedDocs(documentos);
        setProcessedFolios(folios);
        if (documentos.length > 0) {
          setShowSuccessModal(true);
          if (fileInputRef.current) fileInputRef.current.value = '';
          setRows([]);
          setErrors([]);
          setFileName('');
        } else {
          setErrorMessageModal('El servidor no devolvió documentos. Verifica el estado de las facturas.');
          setShowErrorModal(true);
        }
      })
      .catch((error) => {
        setErrorMessageModal('Ocurrió un error al enviar las facturas. Intenta nuevamente.');
        setShowErrorModal(true);
        console.error('Error al enviar las facturas:', error);
      })
      .finally(() => setLoading(false));
  };

  const handleDownloadZip = async () => {
    try {
      setDownloadingZip(true);
      const res = await api.downloadZip({ documentos: JSON.stringify(processedDocs) });
      const url = res.data;
      if (!url) {
        console.error('No se recibió una URL válida para el ZIP');
        return;
      }
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error al descargar el ZIP:', err);
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [...requiredColumns];
    // Ejemplos: neto = cantidad * precio; en afecta el total = neto + IVA, en exenta total = neto.
    const examples = [
      {
        'rut': '12345678-5', 'razon social': 'Comercial Andes SpA', 'giro': 'Venta de equipos',
        'direccion': 'Av. Providencia 123', 'comuna': 'Providencia', 'ciudad': 'Santiago',
        'fecha emision': '2026-06-25', 'nombre producto': 'Servicio de mantencion',
        'cantidad': 1, 'unidad': 'UN', 'precio': 100000, 'iva': 19000,
        'valor total': esExenta ? 100000 : 119000,
        'folio orden compra': 'OC-2026-001', 'fecha orden compra': '2026-06-20',
        'razon orden compra': 'Orden de compra mantencion',
        'folio hes': 'HES-5500', 'fecha hes': '2026-06-22', 'razon hes': 'HES mantencion',
        'email': 'contacto@andes.cl', 'descripcion': 'Mantencion preventiva trimestral', 'forma pago': 'contado',
      },
      {
        'rut': '11111111-1', 'razon social': 'Constructora Sur Ltda', 'giro': 'Construccion',
        'direccion': 'Calle Sur 456', 'comuna': 'Maipu', 'ciudad': 'Santiago',
        'fecha emision': '2026-06-25', 'nombre producto': 'Arriendo maquinaria',
        'cantidad': 2, 'unidad': 'UN', 'precio': 250000, 'iva': 95000,
        'valor total': esExenta ? 500000 : 595000,
        'folio orden compra': 'OC-2026-002', 'fecha orden compra': '2026-06-18',
        'razon orden compra': 'Orden de compra arriendo',
        'folio hes': 'HES-5501', 'fecha hes': '2026-06-21', 'razon hes': 'HES arriendo',
        'email': 'pagos@constructorasur.cl', 'descripcion': '', 'forma pago': 'credito',
      },
    ];
    const aoa = [headers, ...examples.map((r) => headers.map((c) => r[c] ?? ''))];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = headers.map((c) => ({ wch: Math.max(12, COLUMN_LABELS[c]?.length ?? c.length) + 4 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, `plantilla_facturas_${tipoLabel.toLowerCase()}.xlsx`);
  };

  const handleLimpiar = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setRows([]);
    setErrors([]);
    setProcessedFolios([]);
    setProcessedDocs([]);
    setFileName('');
  };

  const stats = useMemo(() => {
    if (rows.length === 0) return null;
    const conError = rows.filter((r) => r.error).length;
    return { total: rows.length, validas: rows.length - conError, conError };
  }, [rows]);

  const formatMiles = (valor) => {
    if (typeof valor !== 'number' || isNaN(valor)) return '';
    return valor.toLocaleString('es-CL');
  };

  return (
    <div className="EmisorMasivo">
      <div className="page-header">
        <h1 className="page-title">Emisor Masivo de Facturas {tipoLabel}s</h1>
        <p className="page-subtitle">
          Sube un Excel con facturas {tipoLabel.toLowerCase()}s y emítelas en lote
          (un producto y dos referencias: Orden de Compra y HES)
        </p>
      </div>

      <div className="search-card">
        <div className="search-card-header">
          <FaCloudUploadAlt className="me-2" />
          Subir archivo Excel
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownloadTemplate}
            className="ms-auto template-btn"
          >
            <FaDownload className="me-2" /><FaFileExcel className="me-1" />Plantilla
          </Button>
        </div>
        <div className="card-body">
          <div
            className={`dropzone ${dragging ? 'dropzone-dragging' : ''} ${fileName ? 'dropzone-with-file' : ''}`}
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current.click(); }}
          >
            {fileName ? (
              <>
                <FaFileExcel className="dropzone-icon dropzone-icon-success" />
                <div className="dropzone-title">{fileName}</div>
                <div className="dropzone-hint">Haz clic para reemplazar el archivo</div>
              </>
            ) : (
              <>
                <FaCloudUploadAlt className="dropzone-icon" />
                <div className="dropzone-title">Arrastra tu Excel aquí o haz clic para seleccionar</div>
                <div className="dropzone-hint">Solo archivos .xlsx o .xls</div>
              </>
            )}
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
          </div>

          <div className="info-box">
            <FaInfoCircle className="info-icon" />
            <div>
              <strong>Columnas requeridas:</strong>
              <div className="info-cols">
                {requiredColumns.map((col) => (
                  <code key={col}>{col}</code>
                ))}
              </div>
              <div className="info-sub">
                El nombre de cada columna debe coincidir exactamente. Todas son obligatorias salvo{' '}
                <code>email</code> y <code>descripcion</code>, que pueden quedar en blanco.{' '}
                {esExenta
                  ? 'En exenta no hay IVA: el valor total debe ser igual a cantidad × precio.'
                  : 'El IVA lo ingresas tú y el valor total debe cuadrar (neto + IVA, con neto = cantidad × precio).'}{' '}
                La forma de pago es obligatoria: contado o crédito. Descarga la plantilla para ver un ejemplo.
              </div>
            </div>
          </div>

          {errors.length > 0 && (
            <div className="alert-box alert-danger-box">
              <FaExclamationTriangle className="alert-box-icon" />
              <div>
                <strong>Errores detectados:</strong>
                <ul className="m-0 mt-1">
                  {errors.map((e, idx) => <li key={idx}>{e}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon-neutral"><FaTable /></div>
            <div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Filas totales</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-success"><FaCheckCircle /></div>
            <div>
              <div className="stat-value">{stats.validas}</div>
              <div className="stat-label">Listas para emitir</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-danger"><FaExclamationCircle /></div>
            <div>
              <div className="stat-value">{stats.conError}</div>
              <div className="stat-label">Con errores</div>
            </div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="search-card mt-4">
          <div className="search-card-header">
            <FaTable className="me-2" />
            Vista previa
            <span className="results-count">{rows.length}</span>
          </div>
          <div className="p-0">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    {requiredColumns.map((col) => (
                      <th key={col} className={NUM_COLUMNS.has(col) ? 'num' : ''}>{COLUMN_LABELS[col]}</th>
                    ))}
                    <th className="num">Neto (calc)</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className={row.error ? 'row-error' : 'row-ok'}>
                      {requiredColumns.map((col) => (
                        <td key={col} className={NUM_COLUMNS.has(col) ? 'num' : ''}>
                          {col === 'rut' && row['rut_display']
                            ? row['rut_display']
                            : NUM_COLUMNS.has(col) && typeof row[col] === 'number'
                              ? formatMiles(row[col])
                              : row[col]}
                        </td>
                      ))}
                      <td className="num">{formatMiles(row._neto)}</td>
                      <td>
                        {row.error ? (
                          <span className="status-pill status-pill-error" title={row.error}>
                            <FaExclamationCircle className="me-1" />
                            {row.error}
                          </span>
                        ) : (
                          <span className="status-pill status-pill-success">
                            <FaCheckCircle className="me-1" />Listo
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="table-footer-actions">
            <Button variant="outline-secondary" onClick={handleLimpiar}>
              <FaEraser className="me-2" />Limpiar
            </Button>
            <Button
              variant="primary"
              className="ms-auto"
              disabled={rows.some((r) => r.error) || loading}
              onClick={handleSend}
            >
              <FaPaperPlane className="me-2" />Emitir facturas {tipoLabel.toLowerCase()}s
            </Button>
          </div>
        </div>
      )}

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-danger me-2" />
            Error al enviar
          </Modal.Title>
        </Modal.Header>
        <Modal.Body><p className="m-0">{errorMessageModal}</p></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCheckCircle className="text-success me-2" />
            Facturas emitidas
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Se procesaron correctamente los siguientes folios:</p>
          <div className="folios-box">
            {processedFolios.length > 0 ? (
              <ul className="folios-list">
                {processedFolios.map((f, idx) => <li key={idx}>Folio <strong>{f}</strong></li>)}
              </ul>
            ) : (
              <em>No se recibieron folios.</em>
            )}
          </div>
          <p className="mt-3 mb-0 text-muted">
            Puedes descargar un ZIP con los PDFs y un Excel con el detalle.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSuccessModal(false)}>Cerrar</Button>
          <Button
            variant="primary"
            onClick={handleDownloadZip}
            disabled={downloadingZip || processedDocs.length === 0}
          >
            {downloadingZip ? (
              <><Spinner animation="border" size="sm" className="me-2" />Preparando…</>
            ) : (
              <><FaFileArchive className="me-2" />Descargar ZIP</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={loading} centered backdrop="static">
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="m-0 modal-message">Cargando facturas, por favor espere…</p>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EmisorMasivoFactura;
